import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NEPBadge } from "@/lib/nep";
import { ArrowRight, FileText, Sparkles, RotateCcw, PlayCircle, CreditCard } from "lucide-react";
import { format } from "date-fns";

const SAMPLE_REPORT_URL = `${process.env.REACT_APP_BACKEND_URL}/assets/sample_report.pdf`;

export default function Dashboard() {
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/results/me"), api.get("/assessment/status")])
      .then(([r, s]) => { setResults(r.data); setStatus(s.data); })
      .finally(() => setLoading(false));
  }, []);

  const hasResults = results.length > 0;

  const priceLabel = status?.price != null ? `₹${status.price.toFixed(2)}` : "";

  let cta = { to: "/assessment", label: "Start Assessment", icon: ArrowRight, heading: "Take your career assessment" };
  if (status?.in_progress) {
    cta = { to: "/assessment", label: "Resume Assessment", icon: PlayCircle, heading: "Continue where you left off" };
  } else if (!status?.has_paid && hasResults) {
    cta = { to: "/pay", label: `Pay ${priceLabel} to Retake`, icon: CreditCard, heading: "Retake your assessment" };
  } else if (!status?.has_paid) {
    cta = { to: "/pay", label: `Pay ${priceLabel} to Start`, icon: CreditCard, heading: "Take your career assessment" };
  } else if (hasResults) {
    cta = { to: "/assessment", label: "Retake Assessment", icon: RotateCcw, heading: "Retake your assessment" };
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-center gap-3">
          <span className="label-mono">Dashboard</span>
          <NEPBadge grade={user?.grade} />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-3">
          Hi, {user?.name}
        </h1>
        <div className="mt-2 text-[#52525B] text-sm flex flex-wrap gap-x-4">
          <span>Class: <b>{user?.grade || "—"}</b></span>
          <span>Board: <b>{user?.education_board || "—"}</b></span>
          <span>School: <b>{user?.school_name || "—"}</b></span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          <div className="md:col-span-7 card-brutal p-8 bg-[#FEF08A]">
            <Sparkles strokeWidth={2.5} size={28} />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-3">
              {cta.heading}
            </h2>
            <p className="mt-2 text-sm md:text-base">
              Questions adapt to your class ({user?.grade || "—"}). PathfinderAiClub writes a report aligned to NEP 2020 and your board.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to={cta.to} data-testid="start-assessment-btn" className="btn-brutal bg-blue-600 text-white inline-flex items-center gap-2 px-5 py-3">
                <cta.icon size={18} strokeWidth={2.5} /> {cta.label}
              </Link>
              <a
                href={SAMPLE_REPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dashboard-sample-report-link"
                className="btn-brutal bg-white inline-flex items-center gap-2 px-5 py-3"
              >
                <FileText size={18} strokeWidth={2.5} /> Sample Report
              </a>
            </div>
          </div>

          <div className="md:col-span-5 card-brutal p-6 bg-[#A7F3D0]">
            <h3 className="font-display text-xl font-bold">What you'll get (NEP-aligned)</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>• Top 3 careers matched to your traits</li>
              <li>• Stream advice with board-specific tips</li>
              <li>• Multidisciplinary subject recommendations</li>
              <li>• Vocational exposure ideas (NEP 2020 § 4.9)</li>
              <li>• 3-stage roadmap: Now, Next, College+</li>
            </ul>
          </div>
        </div>

        {/* Vocational shortcut */}
        <Link to="/vocational" data-testid="dashboard-vocational-card" className="card-brutal p-6 mt-6 flex items-center justify-between bg-[#E9D5FF] hover:-translate-y-1 transition-transform">
          <div>
            <span className="label-mono">NEP § 4.9 · Vocational exposure</span>
            <h3 className="font-display text-lg md:text-xl font-extrabold tracking-tight mt-1">Explore internships & skill courses for Class {user?.grade || "—"}</h3>
            <p className="text-sm mt-1 text-[#52525B]">Curated bootcamps, workshops, and community internships aligned to NEP 2020.</p>
          </div>
          <span className="hidden md:inline btn-brutal bg-white px-4 py-2 text-sm">Open →</span>
        </Link>

        <div className="mt-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="label-mono">Past reports</span>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1">Your history</h2>
            </div>
          </div>

          {loading ? (
            <div className="label-mono">Loading…</div>
          ) : !hasResults ? (
            <div data-testid="no-results" className="card-brutal p-8 text-center bg-white">
              <FileText size={32} strokeWidth={2.5} className="mx-auto" />
              <p className="mt-3 text-sm text-[#52525B]">No assessments yet. Take your first one above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((r) => (
                <Link key={r.id} to={`/report/${r.id}`} data-testid={`result-card-${r.id}`} className="card-brutal p-6 hover:-translate-y-1 transition-transform">
                  <span className="label-mono">{format(new Date(r.created_at), "dd MMM yyyy")}</span>
                  <h3 className="font-display text-lg font-bold mt-2 line-clamp-2">
                    {r.ai_report?.top_careers?.[0]?.title || "Career Report"}
                  </h3>
                  <p className="text-xs text-[#52525B] mt-2 line-clamp-3">{r.ai_report?.summary}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold">
                    View report <ArrowRight size={14} strokeWidth={2.5} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
